import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { SearchableTable } from "@fyrstain/fhir-front-library";
import { useTranslation } from "react-i18next";
import {
  SupportedActorResource,
  SupportedActorType,
} from "../../services/fhir/fhir.types";
import { mapActorResource } from "../../services/fhir/fhir.utils";
import { ScheduleDirectoryProps } from "./ScheduleDirectory.types";
import styles from "./ScheduleDirectory.module.css";

const actorTypes: SupportedActorType[] = [
  "patient",
  "practitioner",
  "location",
  "device",
];

const actorTypeIcons: Record<SupportedActorType, string> = {
  patient: "P",
  practitioner: "Pr",
  location: "L",
  device: "M",
};

const resourceTypeByActorType: Record<SupportedActorType, string> = {
  patient: "Patient",
  practitioner: "Practitioner",
  location: "Location",
  device: "Device",
};

const searchParameterByActorType: Record<SupportedActorType, string> = {
  patient: "name",
  practitioner: "name",
  location: "name",
  device: "device-name",
};

export default function ScheduleDirectory({
  actorType,
  serverUrl,
  onActorTypeChange,
  onActorOpen,
  onError,
}: ScheduleDirectoryProps) {
  const { t } = useTranslation();
  const resourceType = resourceTypeByActorType[actorType];

  return (
    <section className={`${styles.directory} schedule-directory-page`}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>{t("directory.eyebrow")}</span>
          <h1>{t("directory.title")}</h1>
          <p>{t("directory.subtitle")}</p>
        </div>
        <span className={styles.fhirBadge}>{t("directory.fhirBadge")}</span>
      </header>

      <div className={styles.tabs} role="tablist" aria-label={t("directory.tabsLabel")}>
        {actorTypes.map((candidate) => (
          <button
            key={candidate}
            id={`schedule-tab-${candidate}`}
            type="button"
            role="tab"
            aria-selected={candidate === actorType}
            aria-controls="schedule-directory-results"
            className={candidate === actorType ? styles.activeTab : styles.tab}
            onClick={() => onActorTypeChange(candidate)}
          >
            <span>{actorTypeIcons[candidate]}</span>
            {t(`calendar.actorTypes.${candidate}`, { count: 2 })}
          </button>
        ))}
      </div>

      <div
        id="schedule-directory-results"
        className={styles.tableShell}
        role="tabpanel"
        aria-labelledby={`schedule-tab-${actorType}`}
      >
        <SearchableTable
          key={actorType}
          searchCriteriaProperties={{
            title: t("directory.searchLabel"),
            submitButtonLabel: t("directory.search"),
            resetButtonLabel: t("directory.clearSearch"),
            inputs: [
              {
                label: t(`calendar.actorTypes.${actorType}`),
                placeholder: t(`directory.placeholders.${actorType}`),
                type: "text",
                searchParamsName: searchParameterByActorType[actorType],
              },
            ],
          }}
          paginatedTableProperties={{
            columns: [
              {
                header: t(`calendar.actorTypes.${actorType}`),
                dataField: "display",
                width: "70%",
                tabletWidth: "65%",
              },
              {
                header: t("directory.defaultSubtitle"),
                dataField: "subtitle",
                width: "25%",
                tabletWidth: "25%",
              },
            ],
            action: [
              {
                icon: faCalendarDays,
                onClick: (id: string) => onActorOpen(actorType, id),
              },
            ],
            mapResourceToData: (resource: unknown) => {
              const actor = mapActorResource(resource as SupportedActorResource);
              return {
                id: actor.id,
                display: actor.display,
                subtitle:
                  actor.subtitle || t(`calendar.actorTypes.${actor.type}`),
              };
            },
            searchProperties: {
              serverUrl,
              resourceType,
            },
            onError,
          }}
        />
      </div>
    </section>
  );
}
